import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AttachmentOut, MessageOut } from '../../services/api';
import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import type { PendingSend } from '../../hooks/chat/useChatQueries';
import { dayDividerLabel } from './dayDivider';
import { MessageItem } from './MessageItem';

interface MessageListProps {
  messages: MessageOut[];
  pending: PendingSend[];
  currentUserId: string;
  lastReadSeq: number;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  onRetry: (clientId: string, body: string) => void;
  urls?: Record<string, AttachmentUrl | null | undefined>;
  onOpenAttachment?: (tiles: AttachmentOut[], index: number) => void;
  onDownloadAttachment?: (id: string) => void;
}

/** A row's offset from the top of the list's content, whatever the scroll. */
const contentTop = (list: HTMLElement, row: HTMLElement): number =>
  row.getBoundingClientRect().top -
  list.getBoundingClientRect().top +
  list.scrollTop;

export function MessageList({
  messages,
  pending,
  currentUserId,
  lastReadSeq,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
  onRetry,
  urls,
  onOpenAttachment,
  onDownloadAttachment,
}: MessageListProps) {
  const ref = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  // The top row when the older page was asked for, and its offset in the
  // list's content. Once that page renders, scrollTop moves by how far that row
  // moved, so the reader stays on it. A live arrival at the bottom does not
  // move the row, so it does not shift the view.
  const olderAnchorRef = useRef<{ seq: string; top: number } | null>(null);
  const [showNewPill, setShowNewPill] = useState(false);
  const today = new Date();
  const firstUnreadSeq = messages.find(
    m => m.seq > lastReadSeq && m.sender_id !== currentUserId
  )?.seq;

  const scrollToBottom = () => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
    setShowNewPill(false);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      atBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      if (el.scrollTop === 0 && hasOlder && !isLoadingOlder) {
        const row = el.querySelector<HTMLElement>('[data-seq]');
        olderAnchorRef.current = row
          ? { seq: row.dataset.seq ?? '', top: contentTop(el, row) }
          : null;
        onLoadOlder();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasOlder, isLoadingOlder, onLoadOlder]);

  // Before paint, so the view never shows the older rows at the top first.
  // A failed or empty load does not move the row and only clears the anchor.
  useLayoutEffect(() => {
    const el = ref.current;
    const anchor = olderAnchorRef.current;
    if (!el || anchor === null || isLoadingOlder) return;
    olderAnchorRef.current = null;
    const row = el.querySelector<HTMLElement>(`[data-seq="${anchor.seq}"]`);
    if (row) el.scrollTop += contentTop(el, row) - anchor.top;
  }, [messages[0]?.seq, isLoadingOlder]);

  // The list is oldest first, so a channel would open on the oldest page and an
  // arrival would land out of view. Follow the bottom, or offer the pill.
  useEffect(() => {
    if (atBottomRef.current) scrollToBottom();
    else setShowNewPill(true);
  }, [messages[messages.length - 1]?.seq, pending.length]);

  let lastDay = '';
  return (
    <div className='relative flex min-h-0 flex-1 flex-col'>
      <div
        ref={ref}
        data-testid='chat-message-list'
        className='flex-1 overflow-y-auto'
      >
        {messages.map(message => {
          const label = dayDividerLabel(new Date(message.created_at), today);
          const divider = label === lastDay ? null : (lastDay = label);
          return (
            <React.Fragment key={message.seq}>
              {divider && (
                <div className='px-3 py-1 text-center text-[11px] text-muted-foreground'>
                  {divider}
                </div>
              )}
              {message.seq === firstUnreadSeq && (
                <div className='px-3 py-1 text-[11px] font-medium text-primary'>
                  New
                </div>
              )}
              <MessageItem
                message={message}
                isOwn={message.sender_id === currentUserId}
                urls={urls}
                onOpenAttachment={onOpenAttachment}
                onDownloadAttachment={onDownloadAttachment}
              />
            </React.Fragment>
          );
        })}

        {pending.map(p => (
          <div key={p.client_id} className='px-3 py-1.5 bg-primary/[0.06]'>
            <p className='whitespace-pre-wrap break-words text-sm text-muted-foreground'>
              {p.body}
            </p>
            {p.attachment_ids.length > 0 && (
              <p className='text-[11px] text-muted-foreground'>
                {p.attachment_ids.length === 1
                  ? '1 file'
                  : `${p.attachment_ids.length} files`}
              </p>
            )}
            {p.status === 'failed' && (
              <button
                type='button'
                className='text-[11px] text-destructive'
                onClick={() => onRetry(p.client_id, p.body)}
              >
                Not sent · Retry
              </button>
            )}
          </div>
        ))}
      </div>

      {showNewPill && (
        <button
          type='button'
          onClick={scrollToBottom}
          className='absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs text-white'
        >
          New messages ↓
        </button>
      )}
    </div>
  );
}
